import { useState, useMemo, useEffect } from 'react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import Modal from 'src/components/modal/Modal';
import { LineItem } from 'src/types/application/SalesOrderDraftDTO';
import {
  Grid,
  CircularProgress,
  Box,
  List,
  Collapse,
  Tooltip,
} from '@mui/material';
import { Icon } from '@mui/material';
import DataTable from 'src/md-examples/Tables/DataTable';
import { Column } from 'react-table';

import { Autocomplete, TextField } from '@mui/material';

import { useProduct } from 'src/api/inventory-management/products/useProduct';
import useFilteredProductAttributes from 'src/api/inventory-management/products/useFilteredProductAttributes';
import useFamousProductVariantMatches from 'src/api/erp-integrations/famous/useFamousProductVariantMatches';
import VirtualizedVariantList from './VirtualizedVariantList';
import { ProductsV2Autocomplete } from '@/components/products/ProductsV2Autocomplete';
import useCustomerProduct from '@/api/inventory-management/products/useCustomerProduct';
import SelectedVariantSummary from './SelectedVariantSummary';
import ProductVariantListItem from './ProductVariantListItem';
import useOrganizationConfig from '@/api/core/organization-config/useOrganizationConfig';
import { sortProductAttributesByOrder } from '@/api/inventory-management/products/utils';

const AssociateLineItemModal: React.FC<{
  lineItem: LineItem;
  editable?: boolean;
  onClose: (productVariantId?: string) => void;
  customerId: string;
}> = ({ lineItem, onClose, customerId }) => {
  const currentCustomerProductId =
    lineItem.selectedCustomerProductId || lineItem.suggestedCustomerProductId;

  const { data: currentCustomerProduct, error: currentCustomerProductError } =
    useCustomerProduct(currentCustomerProductId, !!currentCustomerProductId);

  const selectedVariantId =
    currentCustomerProduct?.customerProduct.productVariantId;
  const [selectedProductId, setSelectedProductId] = useState<string>(
    currentCustomerProduct?.customerProduct.productId ?? '',
  );

  // Track product attributes for variant matching
  const [selectedAttributes, setSelectedAttributes] = useState<
    Record<string, string>
    // >(lineItem.productAttributes || {});
  >({});
  // Track selected enum option per product attribute id for filtered attributes API
  const [
    selectedAttributeOptionsByAttrId,
    setSelectedAttributeOptionsByAttrId,
  ] = useState<Record<string, string>>({});
  const [showAttributes, setShowAttributes] = useState<boolean>(true);

  // Variant filter state
  const [variantFilter, setVariantFilter] = useState<string>('');

  // Fetch current product data
  const {
    data: currentProductData,
    isLoading: isLoadingCurrentProduct,
    error: currentProductError,
  } = useProduct(
    selectedProductId,
    { includes: ['attributes'] },
    !!selectedProductId,
  );

  // Organization config (contains optional attribute ordering schema)
  const { data: orgConfigData, isLoading: isLoadingOrgConfig } =
    useOrganizationConfig();

  // Fetch filtered attributes (remaining options) based on current enum selections
  const {
    data: filteredAttributesData,
    isLoading: isLoadingFilteredAttributes,
    error: filteredAttributesError,
  } = useFilteredProductAttributes(
    selectedProductId,
    {
      selectedAttributes:
        Object.keys(selectedAttributeOptionsByAttrId).length > 0
          ? selectedAttributeOptionsByAttrId
          : undefined,
      customerId,
    },
    !!selectedProductId,
  );

  // Get variant matches based on selected attributes
  const {
    data: variantMatchesData,
    isLoading: isLoadingVariants,
    error: variantMatchesError,
  } = useFamousProductVariantMatches(selectedProductId, {
    originAttributes:
      Object.keys(selectedAttributes).length > 0
        ? selectedAttributes
        : undefined,
    customerId,
  });

  const error =
    currentProductError ||
    filteredAttributesError ||
    variantMatchesError ||
    currentCustomerProductError;

  // Prefer filtered attributes when available; otherwise keep showing the last known list
  const [lastKnownAttributes, setLastKnownAttributes] = useState(
    currentProductData?.product?.attributes || [],
  );

  const rawAttributes = useMemo(() => {
    const next =
      filteredAttributesData?.attributes ||
      currentProductData?.product?.attributes ||
      lastKnownAttributes ||
      [];
    return next;
  }, [
    filteredAttributesData?.attributes,
    currentProductData?.product?.attributes,
    lastKnownAttributes,
  ]);

  // Resolve preferred order: product-specific > global
  const preferredOrder: string[] | null = useMemo(() => {
    const schema = orgConfigData?.organizationConfig?.productAttributeOrder;
    if (!schema) return null;
    const productId = selectedProductId || currentProductData?.product?.id;

    if (productId && schema.byProductId && schema.byProductId[productId]) {
      return schema.byProductId[productId];
    }
    return schema.global ?? null;
  }, [
    orgConfigData?.organizationConfig?.productAttributeOrder,
    selectedProductId,
    currentProductData?.product?.id,
  ]);

  // Update cache whenever we have a non-empty list
  useEffect(() => {
    if (rawAttributes && rawAttributes.length > 0) {
      setLastKnownAttributes(rawAttributes);
    }
  }, [rawAttributes]);

  const { visibleAttributes, hiddenAttributes } = useMemo(() => {
    const visible: typeof rawAttributes = [];
    const hidden: typeof rawAttributes = [];
    rawAttributes.forEach((attr) => {
      const enumOptions = attr.customField.coreEnum?.options || [];
      const noOptions =
        attr.customField.fieldType === 'enum' && enumOptions.length === 0;
      if (noOptions) hidden.push(attr);
      else visible.push(attr);
    });
    const sortedVisible = sortProductAttributesByOrder(visible, preferredOrder);
    return { visibleAttributes: sortedVisible, hiddenAttributes: hidden };
  }, [rawAttributes, preferredOrder]);

  // Fetch top product variant matches data
  const topVariantIds = useMemo(() => {
    if (!lineItem.topProductVariantMatches) return [];
    return lineItem.topProductVariantMatches
      .toSorted((a, b) => b.confidence - a.confidence)
      .slice(0, 5) // Show top 5 matches
      .map((match) => match.productVariantId);
  }, [lineItem.topProductVariantMatches]);

  // Memoize the column definitions to prevent re-creation on every render
  const lineItemColumns: Column<LineItem>[] = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'customerIdentifier',
        Cell: ({ row }: { row: { original: LineItem } }) => {
          const item = row.original;
          // TODO remove sku and upc
          const identifier = item.customerIdentifier || item.sku || item.upc;

          return identifier ? (
            <MDTypography variant="caption">{identifier}</MDTypography>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          );
        },
      },
      {
        Header: 'Item',
        accessor: 'customerDescription',
        Cell: ({ row }: { row: { original: LineItem } }) => {
          const item = row.original;

          // Try all possible descriptive fields in order of preference
          const description =
            item.customerDescription ||
            item.itemName ||
            item.name ||
            // item.description ||
            // (item.productAttributes &&
            //   Object.entries(item.productAttributes)
            //     .map(([key, value]) => `${key}: ${value}`)
            //     .join(', ')) ||
            'No description available';

          return (
            <MDBox display="flex" flexDirection="column">
              <MDTypography variant="caption" color="text.secondary">
                {description}
              </MDTypography>
            </MDBox>
          );
        },
      },
      {
        Header: 'Unit Price',
        accessor: 'unitPrice',
        Cell: ({ value }: { value?: number | null }) => {
          return <>{value ? `$${value.toFixed(2)}` : ''}</>;
        },
      },
    ],
    [],
  );

  // Memoize the table data to prevent unnecessary re-renders
  const tableData = useMemo(
    () => ({
      columns: lineItemColumns,
      rows: [lineItem],
    }),
    [lineItem, lineItemColumns],
  );

  const handleVariantSelect = (productVariantId?: string | undefined) => {
    onClose(productVariantId);
  };

  const handleAttributeChange = (
    attributeName: string,
    value: string | null,
  ) => {
    const updatedAttributes = { ...selectedAttributes };

    if (value !== null && value !== undefined) {
      updatedAttributes[attributeName] = value;
    } else {
      delete updatedAttributes[attributeName];
    }

    setSelectedAttributes(updatedAttributes);
  };

  const handleEnumSelectionByAttrId = (
    productAttributeId: string,
    optionId: string | null,
  ) => {
    setSelectedAttributeOptionsByAttrId((prev) => {
      const next = { ...prev };
      if (optionId) {
        next[productAttributeId] = optionId;
      } else {
        delete next[productAttributeId];
      }
      return next;
    });
  };

  // Display matching variants information
  const renderMatchingVariantsInfo = () => {
    if (isLoadingVariants) {
      return (
        <Box
          display="flex"
          alignItems="center"
          mt={2}
          mb={1}
          p={1.5}
          bgcolor="rgba(0, 0, 0, 0.04)"
          borderRadius={1}
        >
          <CircularProgress size={16} sx={{ mr: 1.5, color: 'info.main' }} />
          <MDTypography variant="body2" color="text.secondary">
            Finding matching variants...
          </MDTypography>
        </Box>
      );
    }

    if (variantMatchesError) {
      return (
        <Box
          mt={2}
          mb={1}
          p={1.5}
          bgcolor="rgba(211, 47, 47, 0.04)"
          borderRadius={1}
        >
          <MDTypography variant="body2" color="error">
            Error loading variants
          </MDTypography>
        </Box>
      );
    }

    if (
      variantMatchesData?.variants &&
      variantMatchesData.variants.length > 0
    ) {
      const variants = variantMatchesData.variants;

      // If there are too many variants, just show a count
      if (variants.length > 2500) {
        return (
          <Box
            mt={2}
            mb={1}
            p={1.5}
            bgcolor="rgba(25, 118, 210, 0.04)"
            borderRadius={1}
            border="1px solid rgba(25, 118, 210, 0.1)"
          >
            <Box display="flex" alignItems="center">
              <Icon sx={{ color: 'info.main', mr: 1.5, fontSize: 18 }}>
                info_outline
              </Icon>
              <Box>
                <MDTypography
                  variant="body2"
                  fontWeight="medium"
                  color="info.main"
                >
                  {variants.length} matching variants found
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary">
                  Please refine your attribute selections to narrow down the
                  options
                </MDTypography>
              </Box>
            </Box>
          </Box>
        );
      }

      // Show selectable variants list
      return (
        <Box mt={2} mb={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
            px={0.5}
          >
            <Box display="flex" alignItems="center">
              <Icon sx={{ color: 'success.main', mr: 1.5, fontSize: 18 }}>
                check_circle_outline
              </Icon>
              <MDTypography
                variant="body2"
                fontWeight="medium"
                color="success.main"
              >
                {variants.length === 1
                  ? 'One match found'
                  : `${variants.length} matching variants found`}
              </MDTypography>
            </Box>

            {/* Filter input */}
            <Box width="300px">
              <TextField
                size="small"
                fullWidth
                placeholder="Filter matched variants..."
                value={variantFilter}
                onChange={(e) => setVariantFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Icon sx={{ mr: 1, color: 'text.secondary' }}>search</Icon>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                  },
                }}
              />
            </Box>
          </Box>

          {(() => {
            // Filter variants based on the filter text
            const filteredVariants = variants.filter((variant) => {
              if (!variantFilter) return true;

              const filterLower = variantFilter.toLowerCase();

              // Filter by SKU
              if (variant.sku?.toLowerCase().includes(filterLower)) {
                return true;
              }

              // Filter by ID
              if (variant.id.toLowerCase().includes(filterLower)) {
                return true;
              }

              // Filter by NAMEINVC if available in originMetadata
              if (
                variant.originMetadata &&
                typeof variant.originMetadata === 'object'
              ) {
                const metadata = variant.originMetadata as unknown as {
                  NAMEINVC?: string;
                  PRODUCT_ID?: string;
                };
                if (
                  metadata.NAMEINVC &&
                  typeof metadata.NAMEINVC === 'string'
                ) {
                  if (metadata.NAMEINVC.toLowerCase().includes(filterLower)) {
                    return true;
                  }
                }
                if (
                  metadata.PRODUCT_ID &&
                  typeof metadata.PRODUCT_ID === 'string'
                ) {
                  if (metadata.PRODUCT_ID.toLowerCase().includes(filterLower)) {
                    return true;
                  }
                }
              }

              return false;
            });

            // Update the count message
            const countMessage =
              filteredVariants.length === 0
                ? 'No variants match your filter'
                : filteredVariants.length === 1
                  ? '1 variant matches your filter'
                  : `${filteredVariants.length} variants match your filter`;

            return (
              <>
                {variantFilter && (
                  <Box mb={1.5} mt={-1.5} px={0.5} sx={{ textAlign: 'right' }}>
                    <MDTypography variant="caption" color="text.secondary">
                      {countMessage}
                    </MDTypography>
                  </Box>
                )}

                <VirtualizedVariantList
                  variants={filteredVariants}
                  selectedVariantId={selectedVariantId}
                  onSelect={handleVariantSelect}
                  maxHeight={300}
                />
              </>
            );
          })()}
        </Box>
      );
    }

    if (Object.keys(selectedAttributes).length > 0) {
      return (
        <Box
          mt={2}
          mb={1}
          p={1.5}
          bgcolor="rgba(211, 47, 47, 0.04)"
          borderRadius={1}
          display="flex"
          alignItems="center"
        >
          <Icon sx={{ color: 'error.main', mr: 1.5, fontSize: 18 }}>
            highlight_off
          </Icon>
          <MDTypography variant="body2" color="error.main">
            No matching variants found with these attributes
          </MDTypography>
        </Box>
      );
    }

    return null;
  };

  // Display top matches section
  const renderTopMatches = () => {
    if (topVariantIds.length === 0) return null;

    return (
      <Grid item xs={12}>
        <MDBox mb={2}>
          <MDTypography variant="subtitle2" fontWeight="medium">
            Top Matches
          </MDTypography>
          <List>
            {topVariantIds.map((variantId) => {
              return (
                <ProductVariantListItem
                  key={variantId}
                  variantId={variantId}
                  isSelected={selectedVariantId === variantId}
                  onSelect={handleVariantSelect}
                />
              );
            })}
          </List>
        </MDBox>
      </Grid>
    );
  };

  return (
    <Modal
      maxWidth="lg"
      open={true}
      title="Select Product"
      closeable
      error={error}
      hideCloseButton
      onCloseButton={{
        text: 'Cancel',
        onClick: () => {
          onClose();
        },
      }}
    >
      {/* Sticky Customer Request Section */}
      <MDBox
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2,
          mb: 2,
        }}
      >
        <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
          Customer Request
        </MDTypography>
        <MDBox
          p={2}
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
          }}
        >
          <DataTable<LineItem>
            table={tableData}
            canSearch={false}
            showTotalEntries={false}
            isSorted={false}
          />
        </MDBox>
      </MDBox>

      {/* Scrollable Content */}
      <MDBox
        sx={{
          maxHeight: 'calc(70vh - 200px)', // Adjust based on your modal height
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Grid container spacing={2}>
          {/* Show currently selected product variant with clear visual indication */}
          <Grid item xs={12}>
            {selectedVariantId && (
              <SelectedVariantSummary variantId={selectedVariantId} />
            )}
          </Grid>

          {/* Top Product Variant Matches */}
          {renderTopMatches()}

          {/* Product Search */}
          <Grid item xs={12}>
            <MDBox mb={3}>
              <MDTypography variant="subtitle2" fontWeight="medium" mb={2}>
                {selectedVariantId
                  ? 'Select a different product'
                  : topVariantIds.length === 0
                    ? `Match to a product`
                    : `Or Select Another Product`}
              </MDTypography>
              <ProductsV2Autocomplete
                className="w-fit"
                selectedProductId={selectedProductId}
                onProductSelect={(value) => {
                  if (value) {
                    setSelectedProductId(value);
                    setSelectedAttributes({});
                    setShowAttributes(true);
                  }
                }}
              />
            </MDBox>
          </Grid>

          {isLoadingCurrentProduct ||
          isLoadingOrgConfig ||
          isLoadingFilteredAttributes ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              py={2}
              width="100%"
            >
              <CircularProgress size={24} sx={{ mr: 1 }} />
            </Box>
          ) : selectedProductId ? (
            <Grid item xs={12}>
              <Collapse in={showAttributes}>
                <MDBox mb={2}>
                  {isLoadingCurrentProduct &&
                  !filteredAttributesData?.attributes ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <>
                      <MDBox sx={{ position: 'relative' }}>
                        {isLoadingFilteredAttributes && (
                          <Box
                            role="presentation"
                            aria-busy
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              bottom: 0,
                              left: 0,
                              zIndex: 2,
                              cursor: 'progress',
                              pointerEvents: 'all',
                              backgroundColor: 'transparent',
                            }}
                          />
                        )}
                        <Grid container spacing={2} mb={3}>
                          {visibleAttributes.map((attr) => {
                            const enumOptions =
                              attr.customField.coreEnum?.options || [];
                            const noOptions =
                              attr.customField.fieldType === 'enum' &&
                              enumOptions.length === 0;
                            const isDisabled =
                              isLoadingFilteredAttributes || noOptions;
                            return (
                              <Grid item xs={12} sm={6} md={4} key={attr.id}>
                                {attr.customField.fieldType === 'enum' &&
                                attr.customField.coreEnum ? (
                                  <Autocomplete
                                    disabled={isDisabled}
                                    options={[...enumOptions].sort((a, b) =>
                                      (a.name || '').localeCompare(
                                        b.name || '',
                                      ),
                                    )}
                                    getOptionLabel={(option) =>
                                      option.name || ''
                                    }
                                    value={
                                      enumOptions.find(
                                        (opt) =>
                                          opt.id ===
                                          selectedAttributeOptionsByAttrId[
                                            attr.id
                                          ],
                                      ) || null
                                    }
                                    onChange={(_, value) => {
                                      // Update both filtered-attributes state (by attr id)
                                      // and origin-based selection for famous variant matching
                                      if (value === null) {
                                        handleEnumSelectionByAttrId(
                                          attr.id,
                                          null,
                                        );
                                        if (
                                          attr.customField.coreEnum &&
                                          attr.customField.coreEnum.originEntity
                                        ) {
                                          handleAttributeChange(
                                            attr.customField.coreEnum
                                              .originEntity,
                                            null,
                                          );
                                        }
                                      } else {
                                        handleEnumSelectionByAttrId(
                                          attr.id,
                                          value.id,
                                        );
                                        handleAttributeChange(
                                          value.originEntity || '',
                                          value.originId || '',
                                        );
                                      }
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        disabled={isDisabled}
                                        label={attr.customField.fieldName}
                                        size="small"
                                        fullWidth
                                        required={attr.isRequired}
                                        helperText={
                                          attr.customField.description
                                        }
                                      />
                                    )}
                                  />
                                ) : (
                                  <TextField
                                    disabled={isLoadingFilteredAttributes}
                                    label={attr.customField.fieldName}
                                    size="small"
                                    fullWidth
                                    required={attr.isRequired}
                                    value={
                                      selectedAttributes[
                                        attr.customField.fieldName
                                      ] || ''
                                    }
                                    onChange={(e) => {
                                      handleAttributeChange(
                                        attr.customField.fieldName,
                                        e.target.value,
                                      );
                                    }}
                                    helperText={attr.customField.description}
                                  />
                                )}
                              </Grid>
                            );
                          })}
                        </Grid>

                        {hiddenAttributes.length > 0 && (
                          <Box
                            mt={0.5}
                            px={0.5}
                            display="flex"
                            alignItems="center"
                            gap={1}
                          >
                            <Icon
                              sx={{ color: 'text.secondary', fontSize: 18 }}
                            >
                              info_outline
                            </Icon>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Hidden:{' '}
                              {hiddenAttributes
                                .map((a) => a.customField.fieldName)
                                .join(', ')}
                            </MDTypography>
                            <Tooltip
                              title="Attributes are hidden because no options are available for the current selection and this customer's registered variants."
                              placement="top"
                            >
                              <Icon
                                sx={{ color: 'text.secondary', fontSize: 16 }}
                              >
                                help_outline
                              </Icon>
                            </Tooltip>
                          </Box>
                        )}
                      </MDBox>

                      {/* Show matching variants based on selected attributes */}
                      <MDBox>{renderMatchingVariantsInfo()}</MDBox>
                    </>
                  )}
                </MDBox>
              </Collapse>
            </Grid>
          ) : null}

          {/* <Grid item xs={12}>
          <AssociateProductV2
            selectedProductVariantId={selectedVariantId}
            selectedAttributes={selectedAttributes}
            suggestedProductVariantId={lineItem.suggestedProductVariantId}
            topProductVariantMatches={lineItem.topProductVariantMatches}
            onVariantSelected={handleVariantSelect}
          >
            <Grid item xs={12}>
              <span className="text-base font-medium">Customer Request</span>
              <div className="p-2 border border-gray-300 rounded">
                <DataTable<LineItem>
                  table={tableData}
                  canSearch={false}
                  showTotalEntries={false}
                  isSorted={false}
                />
              </div>
            </Grid>
          </AssociateProductV2>
        </Grid> */}
        </Grid>
      </MDBox>
    </Modal>
  );
};

export default AssociateLineItemModal;
